import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Collapse, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useNavigate } from 'react-router-dom';
import { DataProviderInstance } from '../../../api/DataProvider';
import LeadsConfig from '../../../consts/LeadsConfig';
import CreatePatientPopup from '../../../popup/CreatePatientPopup/CreatePatientPopup';
import { buildPatientPrefillFromLead } from '../../../utils/leadUtils';
import { getChatKey, getSendTarget } from '../utils';
import styles from './ContactContextBar.module.css';

const LEAD_STATUS_LABELS = {
  [LeadsConfig.StatusNew]: 'Novo',
  [LeadsConfig.StatusInProgress]: 'Em andamento',
  [LeadsConfig.StatusWaitingResponse]: 'Aguardando resposta',
  [LeadsConfig.StatusFinished]: 'Finalizado',
};

const LEAD_CHANNEL_LABELS = {
  whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  instagram: 'Instagram',
  phone: 'Telefone',
  email: 'E-mail',
  other: 'Outro',
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

const formatMonthYear = (value) => {
  const date = parseDate(value);
  return date ? date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }) : null;
};

const formatDate = (value) => {
  const date = parseDate(value);
  return date ? date.toLocaleDateString('pt-BR') : null;
};

const formatDateTime = (value) => {
  const date = parseDate(value);
  if (!date) return null;
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date.toLocaleDateString('pt-BR')} às ${time}`;
};

// "há 20 min", "há 3 h", "há 2 dias" — ou a data quando já é antigo
const formatElapsed = (value) => {
  const date = parseDate(value);
  if (!date) return null;

  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;

  return formatDate(value);
};

const Item = ({ label, value, danger }) => (
  <span className={`${styles.item} ${danger ? styles.itemDanger : ''}`}>
    <span className={styles.itemLabel}>{label}</span>
    <span className={styles.itemValue}>{value}</span>
  </span>
);

// Indicador do estado recolhido: ícone, número e rótulo curto numa linha só
const MiniStat = ({ icon, value, label, tone }) => (
  <span className={`${styles.miniStat} ${styles[`tone${tone}`]}`}>
    <span className={styles.miniIcon}>{icon}</span>
    <span className={styles.miniValue}>{value}</span>
    <span className={styles.miniLabel}>{label}</span>
  </span>
);

// Indicador do estado expandido: número em destaque sobre fundo pastel
const StatTile = ({ icon, value, label, tone, title }) => (
  <div className={`${styles.tile} ${styles[`tone${tone}`]}`} title={title}>
    <span className={styles.tileIcon}>{icon}</span>
    <div className={styles.tileText}>
      <span className={styles.tileValue}>{value}</span>
      <span className={styles.tileLabel}>{label}</span>
    </div>
  </div>
);

// Ação compacta da barra, no mesmo tom dos demais elementos
const BarAction = ({ icon, label, onClick }) => (
  <button type="button" className={styles.action} onClick={onClick}>
    <span className={styles.actionIcon}>{icon}</span>
    {label}
  </button>
);

const DateBlock = ({ icon, label, value, highlight }) => (
  <div className={`${styles.dateBlock} ${highlight ? styles.dateBlockHighlight : ''}`}>
    <span className={styles.dateIcon}>{icon}</span>
    <div className={styles.dateText}>
      <span className={styles.dateLabel}>{label}</span>
      <span className={styles.dateValue}>{value}</span>
    </div>
  </div>
);

/**
 * Resumo objetivo do contato da conversa aberta (paciente, lead ou sem
 * vínculo), resolvido pelo telefone real no backend.
 *
 * Bloco compacto no cabeçalho: recolhido mostra só os indicadores
 * essenciais numa linha; expandido abre o resumo completo. O resultado
 * fica em cache por conversa, então o polling de mensagens não dispara
 * novas buscas.
 */
const ContactContextBar = ({ chat }) => {
  const { GetWhatsappContactContext } = DataProviderInstance();
  const navigate = useNavigate();

  const chatKey = getChatKey(chat);
  const sendTarget = getSendTarget(chat);
  // Contatos LID ("xxxx@lid") não expõem telefone real — não há o que buscar
  const phoneDigits = (sendTarget || '').includes('@') ? '' : (sendTarget || '').replace(/\D/g, '');
  const canResolve = phoneDigits.length >= 10;

  const cacheRef = useRef({});
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [patientPrefill, setPatientPrefill] = useState(null);
  const [createPatientPopupVisibility, setCreatePatientPopupVisibility] = useState(false);

  useEffect(() => {
    setExpanded(false);

    if (!chatKey || !canResolve) {
      setContext(null);
      setLoading(false);
      return undefined;
    }

    const cached = cacheRef.current[chatKey];
    if (cached) {
      setContext(cached);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setContext(null);
    setLoading(true);

    GetWhatsappContactContext(phoneDigits, (result) => {
      if (!active) return;
      setLoading(false);

      // Falha é silenciosa: o bloco apenas não aparece e o chat segue normal
      const data = result && result.status === 'SUCCESS' ? result.data : null;
      if (!data) return;

      cacheRef.current[chatKey] = data;
      setContext(data);
    });

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatKey, phoneDigits, canResolve, reloadToken]);

  // Após cadastrar o paciente, o contexto precisa ser buscado de novo
  const reloadContext = () => {
    if (chatKey) delete cacheRef.current[chatKey];
    setReloadToken((value) => value + 1);
  };

  const openCreatePatientPopup = (lead) => {
    // Nome do contato só serve de sugestão quando não é o próprio número
    const chatName = (chat?.chatName || '').trim();
    const suggestedName = /^[\d\s()+-]+$/.test(chatName) ? '' : chatName;

    setPatientPrefill(
      buildPatientPrefillFromLead(
        { ...(lead || {}), name: (lead && lead.name) || suggestedName, phone: null },
        phoneDigits
      )
    );
    setCreatePatientPopupVisibility(true);
  };

  const closeCreatePatientPopup = () => {
    setCreatePatientPopupVisibility(false);
    setPatientPrefill(null);
  };

  const patientPopup = createPatientPopupVisibility ? (
    <CreatePatientPopup
      Data={patientPrefill}
      Visibility={true}
      OnChange={reloadContext}
      OnHide={closeCreatePatientPopup}
    />
  ) : null;

  if (!chat) return null;

  if (!canResolve) {
    return (
      <div className={styles.bar}>
        <div className={styles.content}>
          <div className={styles.row}>
            <span className={`${styles.badge} ${styles.badgeNone}`}>Sem telefone</span>
            <span className={styles.hint}>
              O WhatsApp não expôs o número deste contato, então não foi possível identificá-lo.
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.bar}>
        <div className={styles.content}>
          <div className={styles.row}>
            <CircularProgress size={14} />
            <span className={styles.hint}>Carregando contexto do contato…</span>
          </div>
        </div>
      </div>
    );
  }

  if (!context) return null;

  let compact = null;
  let expandedContent = null;
  // Paciente troca o resumo pelo painel completo ao expandir; os demais
  // tipos mantêm a linha compacta e apenas revelam os detalhes abaixo
  let swapOnExpand = false;

  if (context.type === 'patient') {
    const patient = context.patient || {};
    const stats = patient.stats || {};
    const completed = stats.completed || 0;
    const noshow = stats.noshow || 0;
    const canceled = stats.canceled || 0;
    const canceledByPatient = stats.canceledByPatient || 0;
    const canceledTotal = canceled + canceledByPatient;
    const since = formatMonthYear(patient.since);
    const nextDate = patient.nextAppointment ? formatDateTime(patient.nextAppointment.date) : null;
    const lastCompleted = formatDate(patient.lastCompletedDate);
    // O contexto traz apenas o próximo agendamento, não a contagem de futuros
    const scheduled = patient.nextAppointment ? 1 : 0;

    swapOnExpand = true;

    compact = (
      <>
        <span className={`${styles.badge} ${styles.badgePatient}`}>Paciente</span>
        <MiniStat
          tone="Success"
          icon={<EventAvailableOutlinedIcon fontSize="inherit" />}
          value={completed}
          label="realizadas"
        />
        <MiniStat
          tone="Brand"
          icon={<CalendarMonthOutlinedIcon fontSize="inherit" />}
          value={scheduled}
          label={scheduled === 1 ? 'agendada' : 'agendadas'}
        />
        {noshow > 0 && (
          <MiniStat
            tone="Warning"
            icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
            value={noshow}
            label={noshow === 1 ? 'falta' : 'faltas'}
          />
        )}
      </>
    );

    expandedContent = (
      <div className={styles.panel}>
        <div className={styles.tiles}>
          <div className={styles.sinceTile}>
            <span className={styles.tileIcon}>
              <PersonOutlineIcon fontSize="inherit" />
            </span>
            <div className={styles.tileText}>
              <span className={styles.sinceLabel}>Paciente</span>
              <span className={styles.sinceValue}>{since ? `Desde ${since}` : 'Cadastro sem data'}</span>
            </div>
          </div>

          <StatTile
            tone="Success"
            icon={<EventAvailableOutlinedIcon fontSize="inherit" />}
            value={completed}
            label={completed === 1 ? 'Consulta realizada' : 'Consultas realizadas'}
          />
          <StatTile
            tone="Brand"
            icon={<CalendarMonthOutlinedIcon fontSize="inherit" />}
            value={scheduled}
            label={scheduled === 1 ? 'Consulta agendada' : 'Consultas agendadas'}
          />
          <StatTile
            tone={noshow > 0 ? 'Warning' : 'Muted'}
            icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
            value={noshow}
            label={noshow === 1 ? 'Falta' : 'Faltas'}
          />
          <StatTile
            tone="Muted"
            icon={<EventBusyOutlinedIcon fontSize="inherit" />}
            value={canceledTotal}
            label={canceledTotal === 1 ? 'Cancelamento' : 'Cancelamentos'}
            title={canceledByPatient > 0 ? `${canceledByPatient} cancelado(s) pelo paciente` : undefined}
          />
        </div>

        <div className={styles.dates}>
          <DateBlock
            icon={<AccessTimeOutlinedIcon fontSize="inherit" />}
            label="Última consulta"
            value={lastCompleted || 'Nenhuma'}
          />
          <DateBlock
            icon={<CalendarMonthOutlinedIcon fontSize="inherit" />}
            label="Próxima consulta"
            value={nextDate || 'Não agendada'}
            highlight
          />
        </div>

        {noshow > 0 && (
          <div className={styles.alert}>
            <WarningAmberOutlinedIcon fontSize="inherit" className={styles.alertIcon} />
            <span>Atenção ao histórico de faltas antes de confirmar um novo horário.</span>
          </div>
        )}
      </div>
    );
  } else if (context.type === 'lead') {
    const lead = context.lead || {};
    const statusLabel = LEAD_STATUS_LABELS[lead.status] || lead.status;
    const channelLabel = lead.channel ? (LEAD_CHANNEL_LABELS[lead.channel] || lead.channel) : null;
    const firstContact = formatDate(lead.createdAt);
    // Tempo desde a última mensagem vem da própria conversa, não do backend
    const lastMessage = formatElapsed(chat.date);

    compact = (
      <>
        <span className={`${styles.badge} ${styles.badgeLead}`}>Lead</span>
        {statusLabel && <span className={styles.statusChip}>{statusLabel}</span>}
        {firstContact && <Item label="Primeiro contato" value={firstContact} />}
        {lastMessage && <Item label="Última mensagem" value={lastMessage} />}
        <Item label="Agendamento" value={lead.hasAppointment ? 'Vinculado' : 'Sem agendamento'} />
        {lead.patientId ? (
          <BarAction
            icon={<OpenInNewOutlinedIcon fontSize="inherit" />}
            label="Ver paciente"
            onClick={() => navigate(`/patients/${lead.patientId}`)}
          />
        ) : (
          <BarAction
            icon={<PersonAddAlt1OutlinedIcon fontSize="inherit" />}
            label="Cadastrar paciente"
            onClick={() => openCreatePatientPopup(lead)}
          />
        )}
      </>
    );

    expandedContent = (lead.name || channelLabel) ? (
      <div className={`${styles.row} ${styles.detailsRow}`}>
        {lead.name && <Item label="Nome" value={lead.name} />}
        {channelLabel && <Item label="Origem" value={channelLabel} />}
      </div>
    ) : null;
  } else {
    compact = (
      <>
        <span className={`${styles.badge} ${styles.badgeNone}`}>Sem vínculo</span>
        <span className={styles.hint}>
          Este contato ainda não está cadastrado como paciente nem como lead.
        </span>
        <BarAction
          icon={<PersonAddAlt1OutlinedIcon fontSize="inherit" />}
          label="Cadastrar paciente"
          onClick={() => openCreatePatientPopup(null)}
        />
      </>
    );
  }

  const showCompact = !swapOnExpand || !expanded;

  return (
    <div className={styles.bar}>
      {patientPopup}
      <div className={styles.content}>
        {showCompact && <div className={styles.row}>{compact}</div>}

        {expandedContent && (
          <Collapse in={expanded} unmountOnExit>
            {expandedContent}
          </Collapse>
        )}
      </div>

      {expandedContent && (
        <IconButton
          size="small"
          onClick={() => setExpanded((value) => !value)}
          aria-label={expanded ? 'Ocultar detalhes do contato' : 'Ver detalhes do contato'}
          title={expanded ? 'Ocultar detalhes' : 'Ver detalhes'}
        >
          <ExpandMoreIcon
            fontSize="small"
            className={`${styles.expandIcon} ${expanded ? styles.expandIconOpen : ''}`}
          />
        </IconButton>
      )}
    </div>
  );
};

export default ContactContextBar;
